import {
  useAddSkillFromVacancy,
  useCandidate,
  useCandidateVacancyMatch,
} from '@/hooks/use-candidates';
import { usePermission } from '@/lib/permissions';
import { isConsentLocked } from '../lib/consent-actions';
import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';

interface CandidateVacancyMatchCardProps {
  candidateId: string;
  vacancyId: string;
  vacancyTitle: string;
}

export function CandidateVacancyMatchCard({
  candidateId,
  vacancyId,
  vacancyTitle,
}: CandidateVacancyMatchCardProps) {
  const { data: candidate } = useCandidate(candidateId);
  // HW-1055: a candidate who revoked consent is read-only until anonymised.
  const canEdit = usePermission('recruitment.candidates.edit') && !isConsentLocked(candidate);
  const { data, isLoading } = useCandidateVacancyMatch(candidateId, vacancyId);
  const addSkill = useAddSkillFromVacancy(candidateId);

  if (isLoading || !data) {
    return null;
  }

  const totalRequired = data.matchedSkills.length + data.missingSkills.length;

  if (totalRequired === 0) {
    return null;
  }

  return (
    <section className="rounded-lg border border-border bg-background p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        {/* Relies on the profile grid declaring a 0-minimum column (candidate-profile-content):
            in a container sized by its content, this nowrap heading would stretch it to the
            full title instead of truncating. */}
        <h3 className="truncate text-sm font-medium" title={`Skills from ${vacancyTitle}`}>
          <span className="text-muted-foreground">Skills from </span>
          <Link
            to="/recruitment/vacancies/$id"
            params={{ id: vacancyId }}
            className="underline-offset-2 hover:underline"
          >
            {vacancyTitle}
          </Link>
        </h3>
        <span className="shrink-0 text-xs text-muted-foreground">
          {data.matchedSkills.length} / {totalRequired} matched
        </span>
      </div>

      {data.missingSkills.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Candidate already has all required skills.
        </p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {data.missingSkills.map((skill) => (
            <li key={skill.skillId}>
              <button
                type="button"
                disabled={!canEdit || addSkill.isPending}
                onClick={() =>
                  addSkill.mutate({ skillId: skill.skillId, vacancyId })
                }
                className="inline-flex max-w-[200px] cursor-pointer items-center gap-1 rounded-full border border-dashed border-border bg-transparent px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:border-hygge/40 hover:bg-hygge/5 hover:text-hygge disabled:cursor-not-allowed disabled:opacity-50"
                aria-label={`Add ${skill.name}`}
                title={skill.name}
              >
                <Plus className="h-3 w-3 shrink-0" />
                <span className="truncate">{skill.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
